import { toggleFullscreen } from './fullScreenButton.ts';

export const SKIP_SECONDS = 10;
export const VOLUME_STEP = 0.05;

const REPEATABLE_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

export type PlayerHotkeysTarget = {
  paused: () => boolean | undefined;
  play: () => unknown;
  pause: () => void;
  currentTime: (time?: number) => number | undefined;
  volume: (volume?: number) => number | undefined;
  muted: (muted?: boolean) => boolean | undefined;
  isFullscreen: () => boolean | undefined;
  requestFullscreen: () => unknown;
  exitFullscreen: () => unknown;
  isInPictureInPicture?: () => boolean | undefined;
  requestPictureInPicture?: () => unknown;
  exitPictureInPicture?: () => unknown;
  userActive?: (active?: boolean) => void;
};

/**
 * Returns true when the key event should not control the player.
 *
 * @param event - Keyboard event to inspect.
 */
function shouldIgnoreHotkey(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return true;
  if (event.repeat && !REPEATABLE_KEYS.has(event.key)) return true;

  const target = event.target as HTMLElement | null;
  if (!target) return false;

  const tagName = target.tagName;
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    target.isContentEditable
  );
}

/**
 * Clamps player volume to the 0-1 range in 1% increments.
 *
 * @param volume - Volume value to clamp.
 */
function clampVolume(volume: number): number {
  return Math.min(1, Math.max(0, Math.round(volume * 100) / 100));
}

/**
 * Toggles play/pause on the player.
 *
 * @param player - Player instance to control.
 */
function togglePlayPause(player: PlayerHotkeysTarget): void {
  if (player.paused()) {
    player.play();
    return;
  }

  player.pause();
}

/**
 * Seeks the player by a number of seconds.
 *
 * @param params - Player and seek offset.
 */
function skipBySeconds({
  player,
  seconds,
}: {
  player: PlayerHotkeysTarget;
  seconds: number;
}): void {
  const currentTime = player.currentTime() ?? 0;
  player.currentTime(Math.max(0, currentTime + seconds));
}

/**
 * Changes player volume by a 0-1 step and unmutes on increase.
 *
 * @param params - Player and volume delta.
 */
function changeVolume({
  player,
  delta,
}: {
  player: PlayerHotkeysTarget;
  delta: number;
}): void {
  const nextVolume = clampVolume((player.volume() ?? 0) + delta);
  player.volume(nextVolume);

  if (delta > 0 && nextVolume > 0) {
    player.muted(false);
  }
}

/**
 * Toggles mute on the player.
 *
 * @param player - Player instance to control.
 */
function toggleMute(player: PlayerHotkeysTarget): void {
  player.muted(!player.muted());
}

/**
 * Toggles fullscreen using either document fullscreen or the player API.
 *
 * @param params - Player and fullscreen mode.
 */
function togglePlayerFullscreen({
  player,
  useDocumentFullscreen,
}: {
  player: PlayerHotkeysTarget;
  useDocumentFullscreen: boolean;
}): void {
  if (useDocumentFullscreen) {
    toggleFullscreen();
    return;
  }

  if (player.isFullscreen()) {
    player.exitFullscreen();
    return;
  }

  player.requestFullscreen();
}

/**
 * Exits fullscreen using either document fullscreen or the player API.
 *
 * @param params - Player and fullscreen mode.
 */
function exitPlayerFullscreen({
  player,
  useDocumentFullscreen,
}: {
  player: PlayerHotkeysTarget;
  useDocumentFullscreen: boolean;
}): void {
  if (useDocumentFullscreen) {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    return;
  }

  if (player.isFullscreen()) {
    player.exitFullscreen();
  }
}

/**
 * Toggles picture-in-picture when the player supports it.
 *
 * @param player - Player instance to control.
 */
function togglePictureInPicture(player: PlayerHotkeysTarget): void {
  try {
    if (player.isInPictureInPicture?.()) {
      player.exitPictureInPicture?.();
      return;
    }

    player.requestPictureInPicture?.();
  } catch {
    return;
  }
}

/**
 * Handles a player hotkey. Returns true when the event was consumed.
 *
 * @param params - Keyboard event, player, and feature flags.
 */
export function handlePlayerHotkey({
  event,
  player,
  isPipEnabled,
  useDocumentFullscreen,
}: {
  event: KeyboardEvent;
  player: PlayerHotkeysTarget;
  isPipEnabled: boolean;
  useDocumentFullscreen: boolean;
}): boolean {
  if (shouldIgnoreHotkey(event)) return false;

  const key: string =
    event.key.length === 1 ? event.key.toLowerCase() : event.key;

  switch (key) {
    case ' ':
      togglePlayPause(player);
      break;
    case 'ArrowLeft':
      skipBySeconds({ player, seconds: -SKIP_SECONDS });
      break;
    case 'ArrowRight':
      skipBySeconds({ player, seconds: SKIP_SECONDS });
      break;
    case 'ArrowUp':
      changeVolume({ player, delta: VOLUME_STEP });
      break;
    case 'ArrowDown':
      changeVolume({ player, delta: -VOLUME_STEP });
      break;
    case 'm':
      toggleMute(player);
      break;
    case 'f':
      togglePlayerFullscreen({ player, useDocumentFullscreen });
      break;
    case 'Escape':
      exitPlayerFullscreen({ player, useDocumentFullscreen });
      break;
    case 'i':
      if (!isPipEnabled) return false;
      togglePictureInPicture(player);
      break;
    default:
      return false;
  }

  player.userActive?.(true);

  if (key !== 'Escape') {
    event.preventDefault();
    event.stopPropagation();
  }

  return true;
}

/**
 * Attaches player hotkeys to the document while the player is active.
 *
 * @param params - Player and feature flags.
 * @returns Cleanup function that removes the listener.
 */
export function setupPlayerHotkeys({
  player,
  isPipEnabled,
  useDocumentFullscreen,
}: {
  player: PlayerHotkeysTarget;
  isPipEnabled: boolean;
  useDocumentFullscreen: boolean;
}): () => void {
  const onKeyDown = (event: KeyboardEvent): void => {
    handlePlayerHotkey({
      event,
      player,
      isPipEnabled,
      useDocumentFullscreen,
    });
  };

  document.addEventListener('keydown', onKeyDown, true);

  return () => {
    document.removeEventListener('keydown', onKeyDown, true);
  };
}
